import { Box, SimpleGrid, createStyles } from '@mantine/core';
import type { BarChartConfig, BiMetricConfig } from '@repo/api-codegen';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import { entries, first, pickBy } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';
import { observer } from 'mobx-react-lite';
import { memo } from 'react';
import { toTitleCase } from '../../../utils/charts/utils';
import type { ConfigTransformer } from '../types';
import { getAxesSettings } from './ChartTypeSelector.utils';

const useStyles = createStyles((theme) => ({
	chartCard: {
		width: '100%',
		height: theme.other.space[12],
		padding: theme.other.space[1.5], // 6px padding
		borderRadius: theme.other.borderRadius.sm,
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		border: `0.5px solid ${theme.other.getColor('border/input/default')}`,
		backgroundColor: theme.other.getColor('surface/primary/default'),
		// transition: theme.other.transition.fast,

		'&:hover': {
			backgroundColor: theme.other.getColor('surface/primary/hover'),
			borderColor: theme.other.getColor('border/input/active'),
		},
	},

	chartCardSelected: {
		backgroundColor: theme.other.getColor('surface/primary/selected'),
		border: `1px solid ${theme.other.getColor('border/input/active')}`,

		'&:hover': {
			backgroundColor: theme.other.getColor('surface/primary/selected'),
			borderColor: theme.other.getColor('border/input/active'),
		},
	},

	chartCardDisabled: {
		opacity: 0.5,
		cursor: 'not-allowed',

		'&:hover': {
			backgroundColor: theme.other.getColor('surface/primary/default'),
			borderColor: theme.other.getColor('border/input/default'),
		},
	},

	iconContainer: {
		color: theme.other.getColor('icon/secondary/default'),
		marginBottom: theme.other.space[0.5], // 2px
	},

	iconContainerSelected: {
		color: theme.other.getColor('icon/primary/default'),
	},

	textContainer: {
		fontSize: '11px',
		fontWeight: 500,
		lineHeight: '16px',
		color: theme.other.getColor('text/secondary/default'),
		textAlign: 'center',
	},

	textContainerSelected: {
		color: theme.other.getColor('text/primary/default'),
	},
}));

interface ChartTypeOption {
	id: string;
	label: string;
	icon: IconNames | React.ComponentType<{ size?: number; stroke?: number }>;
	transformer: ConfigTransformer;
}

const convertToLineChart = (
	config: BiMetricConfig['chart_config'],
	percentageStacked: boolean
): BiMetricConfig['chart_config'] => {
	const result = cloneDeep(config) as BiMetricConfig['chart_config'] & {
		axes?: {
			x: unknown[];
			y: unknown[];
			category?: unknown[];
		};
		chartType?: string;
		lineGrouping?: string;
	};
	const axesSettings = getAxesSettings(config);

	result.chartType = 'line';
	if (!result.axes) {
		result.axes = {
			x: [],
			y: [],
			category: [],
		};
	}
	if (axesSettings) {
		result.axes.x = axesSettings.x;
		result.axes.y = axesSettings.y;
		result.axes.category = axesSettings.category || [];
	}

	if (percentageStacked) {
		result.lineGrouping = 'percentage-stack';
	} else {
		result.lineGrouping = 'default';
	}

	return result;
};

const convertToBarChart = ({
	config,
	horizontal,
	barGroupType,
}: {
	config: BiMetricConfig['chart_config'];
	horizontal: boolean;
	barGroupType: 'stack' | 'default' | 'percentage-stack';
}): BiMetricConfig['chart_config'] => {
	const result = cloneDeep(config) as BiMetricConfig['chart_config'] & {
		axes?: {
			x: unknown[];
			y: unknown[];
			category?: unknown[];
		};
		chartType?: string;
		barLayout?: string;
		barGrouping?: string;
	};
	const axesSettings = getAxesSettings(config);

	result.chartType = 'bar';
	if (!result.axes) {
		result.axes = {
			x: [],
			y: [],
			category: [],
		};
	}

	if (axesSettings) {
		result.axes.x = axesSettings.x;
		result.axes.y = axesSettings.y;
		result.axes.category = axesSettings.category || [];
	}

	if (horizontal) {
		result.barLayout = 'horizontal';
	} else {
		result.barLayout = 'vertical';
	}

	result.barGrouping = barGroupType as 'default' | 'stack' | 'percentage-stack';

	return result;
};

const convertToPieChart = (
	config: BiMetricConfig['chart_config']
): BiMetricConfig['chart_config'] => {
	const result = cloneDeep(config) as BiMetricConfig['chart_config'] & {
		axes?: {
			x: unknown[];
			y: unknown[];
		};
		chartType?: string;
	};
	const axesSettings = getAxesSettings(config);

	result.chartType = 'pie';
	if (!result.axes) {
		result.axes = {
			x: [],
			y: [],
		};
	}

	if (axesSettings) {
		result.axes.x = axesSettings.x;
		result.axes.y = axesSettings.y;
	}

	return result;
};

const convertToScatterChart = (
	config: BiMetricConfig['chart_config']
): BiMetricConfig['chart_config'] => {
	const result = cloneDeep(config) as any;
	const axesSettings = getAxesSettings(config);
	result.chartType = 'scatter';

	if (!result.axes) {
		result.axes = {
			x: [],
			y: [],
			category: [],
			size: [],
		};
	}

	if (axesSettings) {
		result.axes.x = axesSettings.x;
		result.axes.y = axesSettings.y;
		result.axes.category = axesSettings.category || [];
		result.axes.size = axesSettings.size || [];
	}

	return result;
};

const convertToTableChart = (
	config: BiMetricConfig['chart_config']
): BiMetricConfig['chart_config'] => {
	const result = cloneDeep(config) as BiMetricConfig['chart_config'] & {
		chartType?: string;
	};

	result.chartType = 'table';

	// Set compactNumbers: false for all number columns in table charts
	if (result.columnConfigs) {
		Object.keys(result.columnConfigs).forEach((columnName) => {
			if (
				result.columnConfigs[columnName] &&
				result.columnConfigs[columnName].columnType === 'number'
			) {
				result.columnConfigs[columnName] = {
					...result.columnConfigs[columnName],
					compactNumbers: false,
				};
			}
		});
	}

	return result;
};

const convertToNumberChart = (
	config: BiMetricConfig['chart_config']
): BiMetricConfig['chart_config'] => {
	const result = cloneDeep(config) as BiMetricConfig['chart_config'] & {
		chartType?: string;
		numberColumnName?: string;
		numberHeader?: string;
	};
	result.chartType = 'number';
	const firstNumberColumn = first(
		entries(
			pickBy(result.columnConfigs, (value) => value.columnType === 'number')
		)
	);

	if (!result.numberColumnName) {
		result.numberColumnName = firstNumberColumn?.[0] || '';
		result.numberHeader = toTitleCase(firstNumberColumn?.[0] || '');
	}

	// Set compactNumbers: false for all number columns in number charts
	if (result.columnConfigs) {
		Object.keys(result.columnConfigs).forEach((columnName) => {
			if (
				result.columnConfigs[columnName] &&
				result.columnConfigs[columnName].columnType === 'number'
			) {
				result.columnConfigs[columnName] = {
					...result.columnConfigs[columnName],
					compactNumbers: false,
				};
			}
		});
	}

	return result;
};

const chartTypeOptions: ChartTypeOption[] = [
	{
		id: 'line',
		label: 'Line',
		icon: 'lineChart',
		transformer: (config) => convertToLineChart(config, false),
	},
	// TODO: Add stacked line chart
	// {
	// 	id: 'stacked-line',
	// 	label: 'Stacked line',
	// 	icon: 'stackedLineChart',
	// 	transformer: (config) => convertToLineChart(config, true),
	// },
	{
		id: 'bar',
		label: 'Column',
		icon: 'columnChart',
		transformer: (config) =>
			convertToBarChart({ config, horizontal: false, barGroupType: 'default' }),
	},
	{
		id: 'stacked-bar',
		label: 'Stacked column',
		icon: 'stackColumnChart',
		transformer: (config) =>
			convertToBarChart({ config, horizontal: false, barGroupType: 'stack' }),
	},
	{
		id: 'horizontal-bar',
		label: 'Bar',
		icon: 'barChart',
		transformer: (config) =>
			convertToBarChart({ config, horizontal: true, barGroupType: 'default' }),
	},
	{
		id: 'horizontal-stacked-bar',
		label: 'Stacked bar',
		icon: 'stackBarChart',
		transformer: (config) =>
			convertToBarChart({ config, horizontal: true, barGroupType: 'stack' }),
	},
	{
		id: 'pie',
		label: 'Donut',
		icon: 'chartDonut',
		transformer: convertToPieChart,
	},
	{
		id: 'scatter',
		label: 'Scatter',
		icon: 'scatterChart',
		transformer: convertToScatterChart,
	},
	{
		id: 'table',
		label: 'Table',
		icon: 'table',
		transformer: convertToTableChart,
	},
	{
		id: 'number',
		label: 'Number',
		icon: 'hash',
		transformer: convertToNumberChart,
	},
];

const determineCurrentChartType = (config: BiMetricConfig['chart_config']) => {
	if (
		['metric', 'scatter', 'pie', 'table', 'line', 'number'].includes(
			config.chartType
		)
	) {
		// Check for stacked line
		if (config.chartType === 'line') {
			const lineConfig = config as BiMetricConfig['chart_config'] &
				Record<string, unknown>;
			if (lineConfig.lineGrouping === 'percentage-stack') {
				return 'stacked-line';
			}
		}
		return config.chartType;
	}

	if (config.chartType === 'bar') {
		const barConfig = config as BarChartConfig;
		if (barConfig.barLayout === 'horizontal') {
			if (barConfig.barGrouping === 'stack') {
				return 'horizontal-stacked-bar';
			}

			if (barConfig.barGrouping === 'percentage-stack') {
				return 'horizontal-percentage-stacked-bar';
			}

			return 'horizontal-bar';
		}

		if (barConfig.barGrouping === 'stack') {
			return 'stacked-bar';
		}

		if (barConfig.barGrouping === 'percentage-stack') {
			return 'percentage-stacked-bar';
		}

		return 'bar';
	}

	return 'line';
};

const ChartTypeCard = memo(
	({
		option,
		isSelected,
		onClick,
		isDisabled = false,
	}: {
		option: ChartTypeOption;
		isSelected: boolean;
		onClick: () => void;
		isDisabled?: boolean;
	}) => {
		const { classes, cx } = useStyles();

		const renderIcon = () => {
			if (typeof option.icon === 'string') {
				// It's an IconNames string, use the Icon component
				return <Icon name={option.icon} size="md" />;
			} else {
				// It's a React component, render it directly
				const IconComponent = option.icon;
				return <IconComponent size={16} stroke={1.5} />;
			}
		};

		return (
			<Box
				className={cx(
					classes.chartCard,
					isSelected && classes.chartCardSelected,
					isDisabled && classes.chartCardDisabled
				)}
				onClick={isDisabled ? undefined : onClick}
				role="button"
				aria-label={`Select ${option.label}`}
				aria-pressed={isSelected}
				aria-disabled={isDisabled}
				tabIndex={isDisabled ? -1 : 0}
				onKeyDown={(e) => {
					if (isDisabled) return;
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onClick();
					}
				}}
			>
				<Box
					className={cx(
						classes.iconContainer,
						isSelected && classes.iconContainerSelected
					)}
				>
					{renderIcon()}
				</Box>
				<Text
					lineClamp={1}
					className={cx(
						classes.textContainer,
						isSelected && classes.textContainerSelected
					)}
				>
					{option.label}
				</Text>
			</Box>
		);
	}
);

interface ChartTypeSelectorProps {
	chartConfig: BiMetricConfig['chart_config'];
	onUpdate: (config: BiMetricConfig['chart_config']) => void;
	results: Record<string, unknown>[];
}

export const ChartTypeSelector = observer(
	({ chartConfig, onUpdate }: ChartTypeSelectorProps) => {
		const selectedType = determineCurrentChartType(chartConfig);

		const handleChartTypeSelect = (transformer: ConfigTransformer) => {
			const updatedConfig = transformer(chartConfig);
			onUpdate(updatedConfig);
		};

		// Determine if chart type should be disabled based on data
		const isChartTypeDisabled = () => false; // Placeholder - implement based on your requirements

		return (
			<SimpleGrid cols={3} spacing={8} w="100%">
				{chartTypeOptions.map((option) => (
					<ChartTypeCard
						key={option.id}
						option={option}
						isSelected={selectedType === option.id}
						isDisabled={isChartTypeDisabled()}
						onClick={() => handleChartTypeSelect(option.transformer)}
					/>
				))}
			</SimpleGrid>
		);
	}
);
